import * as t from 'io-ts';
import React, {FC, useEffect, useState} from 'react';
import {connect} from 'react-redux';

import {addQueryToPath} from '../../../../libs/shared/core/src/helpers/browser/urlHelpers';
import {usePrevious} from '../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {makeApiType} from '../../../../libs/shared/core/src/models/api/apiTypes';
import {isTeammateActive} from '../../../../libs/shared/core/src/models/helpers/teammateModelHelpers';
import {fetchApiAsync} from '../../../../libs/shared/core/src/services/http/http';
import {buildAppQueries} from '../../../../libs/shared/core/src/services/query/appQueryHelpers';
import {
  OnboardingEventsEnum,
  trackEvent,
  TrackingTypesEnum,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {ResolveStateProps} from '../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ResolveDispatchProps} from '../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {AppPanelId} from '../../../../libs/shared/core/src/store/panels/panelStateBase';
import {makeMapDispatchToProps, makeMapStateToProps} from '../../../../srcCommon/helpers/reselectHelpers';
import {useAppSelector} from '../../../helpers/appReduxHelpers';
import {completeCompanyOnboardingAction} from '../../../store/data/company/companyActions';
import {
  saveWasOnboardingCompletedAction,
  updateOnboardingChecklistVersionForExperimentAction,
} from '../../../store/data/preferences/preferencesActions';
import {getWasOnboardingCompleted} from '../../../store/data/preferences/preferencesSelectors';
import {navigateToExpandedSandboxInboxAction} from '../../../store/data/teammates/teammatesActions';
import {getIsUserFirstGlobalAdmin, getUser} from '../../../store/data/teammates/teammatesSelectors';
import {
  updateDepartmentMetadataAction,
  updateIndustryMetadataAction,
} from '../../../store/data/userMetadata/userMetadataActions';
import {getHasFirstGlobalAdminWelcomeCarousel} from '../../../store/stateless/welcomeCarouselSelectors';
import {useOnboardingModalState} from '../../app/topBar/onboarding/onboardingContext';
import {WelcomeCarouselContainer} from '../../carousel/welcome/welcomeCarouselContainer';
import {ExpandedDarkThemeProvider} from '../../common/theme/expandedDarkThemeProvider';
import {OnboardingVideoPopover} from './onboardingVideoPopover';

/*
 * Props.
 */

interface OnboardingWelcomeContainerProps {
  panelId: AppPanelId;
}

type OnboardingWelcomeContainerComponentProps = OnboardingWelcomeContainerProps &
  ResolveStateProps<typeof mapStateToProps> &
  ResolveDispatchProps<typeof mapDispatchToProps>;

/*
 * Component.
 */

const OnboardingWelcomeContainerComponent: FC<OnboardingWelcomeContainerComponentProps> = (props) => {
  const {
    hasFirstGlobalAdminWelcome,
    saveWasOnboardingCompleted,
    completeCompanyOnboarding,
    updateOnboardingChecklistVersionForExperiment,
    navigateToSandboxInbox,
  } = props;
  const {hash} = window.location;

  const wasOnboardingCompleted = useAppSelector(getWasOnboardingCompleted);
  const isUserFirstGlobalAdmin = useAppSelector(getIsUserFirstGlobalAdmin);
  const user = useAppSelector(getUser);

  // Show guided onboarding when the user is the first global admin and hasn't completed onboarding.
  const showGuidedOnboarding = hash === '#show-guided-onboarding' || hasFirstGlobalAdminWelcome;
  const [shouldShowGuidedOnboarding, setShouldShowGuidedOnboarding] = useState(showGuidedOnboarding);

  // If onboarding isn't complete, show the old onboarding when the user isn't the first global admin.
  const shouldShowOldOnboarding =
    isTeammateActive(user) && !isUserFirstGlobalAdmin && !wasOnboardingCompleted;
  const previousShouldShowGuidedOnboarding = usePrevious(shouldShowGuidedOnboarding);
  const {isAdminSetupGuideOpen} = useOnboardingModalState();

  useEffect(() => {
    if (showGuidedOnboarding) {
      trackEvent(TrackingTypesEnum.ONBOARDING_STEP, OnboardingEventsEnum.STARTED);
      trackEvent(TrackingTypesEnum.ONBOARDING_STEP, OnboardingEventsEnum.STARTED_VIDEO);
    }
  }, [showGuidedOnboarding]);

  useEffect(() => {
    const updateOnboardingPreferences = async () => {
      await updateOnboardingChecklistVersionForExperiment();
      await saveWasOnboardingCompleted();
    };
    const finishOnboarding = async () => {
      // Open the p0 checklist after all onboarding preferences are already set.
      updateOnboardingPreferences();
      completeCompanyOnboarding();
      trackEvent(TrackingTypesEnum.ONBOARDING_STEP, OnboardingEventsEnum.COMPLETED);
    };
    if (previousShouldShowGuidedOnboarding && !shouldShowGuidedOnboarding) {
      finishOnboarding();
    }
  }, [
    completeCompanyOnboarding,
    previousShouldShowGuidedOnboarding,
    saveWasOnboardingCompleted,
    shouldShowGuidedOnboarding,
    updateOnboardingChecklistVersionForExperiment,
  ]);

  if (isAdminSetupGuideOpen) {
    // The AdminSetupGuideButton component will render the modal. Don't render anything here.
    return null;
  }

  if (shouldShowOldOnboarding) {
    return (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <WelcomeCarouselContainer {...props} />
    );
  }

  if (shouldShowGuidedOnboarding) {
    return (
      <ExpandedDarkThemeProvider>
        <OnboardingVideoPopover
          onSubmit={() => {
            navigateToSandboxInbox();
            setShouldShowGuidedOnboarding(false);
          }}
        />
      </ExpandedDarkThemeProvider>
    );
  }

  return null;
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps()(() => ({
  hasFirstGlobalAdminWelcome: getHasFirstGlobalAdminWelcomeCarousel,
}));

const mapDispatchToProps = makeMapDispatchToProps()(() => ({
  updateDepartmentMetadata: updateDepartmentMetadataAction,
  updateIndustryMetadata: updateIndustryMetadataAction,
  saveWasOnboardingCompleted: saveWasOnboardingCompletedAction,
  completeCompanyOnboarding: completeCompanyOnboardingAction,
  updateOnboardingChecklistVersionForExperiment: updateOnboardingChecklistVersionForExperimentAction,
  navigateToSandboxInbox: navigateToExpandedSandboxInboxAction,
}));

export const OnboardingWelcomeContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(OnboardingWelcomeContainerComponent);

const RCompletedCourses = t.record(t.string, t.boolean);

const parseCompletedCourses = (src: any) => src.completed_courses;

const ApiCompletedCourses = makeApiType('completedCourses', RCompletedCourses, parseCompletedCourses);

const RCompletedCourse = t.type({
  courseUuid: t.string,
});

const parseCompletedCourse = (src: any) => ({courseUuid: src.course_uuid});

export const ApiCompletedCourse = makeApiType('completedCourse', RCompletedCourse, parseCompletedCourse);

export const lmsCompletedCoursesQueries = buildAppQueries('lmsCompletedCourses', {
  forCourseUuids: (courseUuids: ReadonlyArray<string>) => ({
    queryKey: courseUuids,
    queryFn: async ({company, cancelToken}) =>
      fetchApiAsync(
        ApiCompletedCourses,
        addQueryToPath(`${company.url}/lms/completed-courses`, {
          courses: courseUuids.join(','),
        }),
        {cancelToken},
      ),
  }),
});
